import {
  HelpFeedbackInputSchema,
  SchemaValidationFailedError,
  type HelpFeedbackInput,
} from '@kilat/shared';

/**
 * Anonymous help-manual feedback writes — `helpFeedback/{autoId}`.
 *
 * Design choice: a direct client-SDK `addDoc` (the marketing app already
 * ships a Firestore client for platformBrand reads) rather than a callable —
 * `infra/firestore.rules` re-validates every key/type/size server-side, so a
 * Cloud Function in front of a fire-and-forget write would be new machinery
 * for no extra safety. Lives under lib/repositories/ so the app's
 * no-direct-firestore lint boundary stays intact.
 *
 * `createdAt` is always serverTimestamp() — the rules REQUIRE request.time,
 * so a client can't backdate votes.
 *
 * Firestore is imported inside the function. `feedback.tsx` renders a thumbs
 * widget under every help article, so a static import put the SDK on the
 * critical path of `/help` — 354 KB fetched at first paint in case the reader
 * later pressed a thumb. Measured 2026-08-23; same defect as the callable
 * wrappers in `../callables/_lazy.ts`.
 */
export async function submitHelpFeedback(input: HelpFeedbackInput): Promise<void> {
  const parsed = HelpFeedbackInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('helpFeedback.input', parsed.error.flatten());
  }
  const [{ addDoc, collection, serverTimestamp }, { db }] = await Promise.all([
    import('firebase/firestore'),
    import('../firebase.js'),
  ]);
  await addDoc(collection(db, 'helpFeedback'), {
    ...parsed.data,
    createdAt: serverTimestamp(),
  });
}
